import { normalizeEloHeights } from "./C1_EloHeights";
import type { C1_ModelSettingsData } from "../types/C1_ModelTypes";
import type { C1_Project } from "./C1_Project";
import { C1_DEFAULT_BUTTON_DEFAULTS, type C1_ButtonDefaultsByType } from "../types/C1_EloTypes";
import BaseModel from "../../../src/models/BaseModel";
import { c1_state } from "./C1_runtimeContext";
import { normalizeEloItemScale } from "../plan/C1_eloItemScale";

export const C1_FURNISHING_WALL_LIGHTEN_FACTOR = 0.55;
export const C1_FURNISHING_FURNITURE_DARKEN_FACTOR = 0.2;
export const C1_FURNISHING_FURNITURE_FILL_COLOR = "#e4edc0";
export const C1_HIDE_ROOM_CONTENTS_WHEN_SELECTED = false;

export const C1_DEFAULT_MODEL_SETTINGS: C1_ModelSettingsData = {
  selectObjectsBeforeMoving: false,
  showTags: false,
  tagByIndex: true,
  tagEditable: false,
  supportsDALI: false,
  defaultOpeningShading: "jalousie",
  defaultOpeningContact: false,
  wallThicknessExterior: 0.4,
  wallThicknessInterior: 0.16,
  wallColorExterior: "#9a9a9a",
  wallColorInterior: "#9a9a9a",
  wallStrokeWidth: 1,
  furnitureStrokeWidth: 1,
  furnitureStrokeColor: "#ddd",
  furnitureFillColor: "#fff",
  floorColorGeneral1: "#eee8dc",
  floorColorGeneral2: "#dce5e8",
  floorFinishOpacity: 50,
  eloGraphicContrast: 115,
  eloItemScale: 1,
};

export class C1_ModelSettings extends BaseModel {
  protected _data: C1_ModelSettingsData;

  constructor(data: Partial<C1_ModelSettingsData> = {}, _project?: C1_Project) {
    super();
    this._data = { ...C1_DEFAULT_MODEL_SETTINGS, ...data, eloHeights: normalizeEloHeights(data.eloHeights) };
  }

  get data(): Readonly<C1_ModelSettingsData> {
    return this._data;
  }
  get className(): string {
    return "C1_ModelSettings";
  }

  override fromJSON(input: unknown): void {
    this._data = {
      ...C1_DEFAULT_MODEL_SETTINGS,
      ...(input as Partial<C1_ModelSettingsData>),
      eloHeights: normalizeEloHeights((input as Partial<C1_ModelSettingsData>)?.eloHeights),
    };
  }

  get eloHeights() {
    return normalizeEloHeights(this.data.eloHeights);
  }

  get selectObjectsBeforeMoving() {
    return this.data.selectObjectsBeforeMoving ?? false;
  }

  get showTags() {
    // Die Sichtbarkeit ist eine Eigenschaft des aktuellen Benutzermodus und
    // keine frei konfigurierbare Projekteinstellung: Tags gehören in Elektro.
    return c1_state.project?.displayMode.value === "technical";
  }

  get tagByIndex() {
    return globalThis.state?.settings?.c1TagByIndex ?? this.data.tagByIndex ?? true;
  }

  get tagEditable() {
    return globalThis.state?.settings?.c1TagEditable ?? this.data.tagEditable ?? false;
  }

  get supportsDALI() {
    return this.data.supportsDALI ?? false;
  }


  get defaultOpeningShading() {
    return this.data.defaultOpeningShading ?? "jalousie";
  }

  get defaultOpeningContact() {
    return this.data.defaultOpeningContact ?? false;
  }

  get wallThicknessExterior() {
    return this.data.wallThicknessExterior;
  }

  get wallThicknessInterior() {
    return this.data.wallThicknessInterior;
  }

  get wallColorExterior() {
    return this.data.wallColorExterior;
  }

  get wallColorInterior() {
    return this.data.wallColorInterior;
  }

  get wallStrokeWidth() {
    return this.data.wallStrokeWidth;
  }

  get furnitureStrokeWidth() {
    return this.data.furnitureStrokeWidth;
  }

  get furnitureStrokeColor() {
    return this.data.furnitureStrokeColor;
  }

  get furnitureFillColor() {
    return this.data.furnitureFillColor;
  }

  get floorColorGeneral1() {
    return this.data.floorColorGeneral1 ?? "#eee8dc";
  }

  get floorColorGeneral2() {
    return this.data.floorColorGeneral2 ?? "#dce5e8";
  }

  get floorFinishOpacity() {
    return Math.min(100, Math.max(0, this.data.floorFinishOpacity ?? 50));
  }

  get eloGraphicContrast() {
    return Math.min(180, Math.max(100, this.data.eloGraphicContrast ?? 115));
  }

  get eloItemScale() {
    return normalizeEloItemScale(this.data.eloItemScale);
  }

  // Compatibility for equipment factories; no longer a configurable project setting.
  get buttonDefaults(): C1_ButtonDefaultsByType {
    return {
      simple: { ...C1_DEFAULT_BUTTON_DEFAULTS.simple },
      touch: { ...C1_DEFAULT_BUTTON_DEFAULTS.touch },
    };
  }

  update(values: Partial<C1_ModelSettingsData>) {
    this.beforeChange("settings");
    if (globalThis.state?.settings) {
      if (values.tagByIndex !== undefined) state.settings.c1TagByIndex = values.tagByIndex;
      if (values.tagEditable !== undefined) state.settings.c1TagEditable = values.tagEditable;
    }
    Object.assign(this._data, values);
    this.changed("settings");
  }

  toJSON(): C1_ModelSettingsData {
    const { buttonDefaults: _legacyButtonDefaults, useIntermediateDistribution: _removedFlag, ...data } =
      this.data as C1_ModelSettingsData & { useIntermediateDistribution?: boolean };
    return {
      ...data,
      eloHeights: this.eloHeights,
      selectObjectsBeforeMoving: this.selectObjectsBeforeMoving,
      showTags: this.showTags,
      tagByIndex: this.tagByIndex,
      tagEditable: this.tagEditable,
      supportsDALI: this.supportsDALI,
      defaultOpeningShading: this.defaultOpeningShading,
      defaultOpeningContact: this.defaultOpeningContact,
      eloGraphicContrast: this.eloGraphicContrast,
      eloItemScale: this.eloItemScale,
    };
  }
}
